import React from 'react';
import { Bell, BellOff, Loader2, CheckCircle2, XCircle, Smartphone, Share, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { getPlatformInfo } from '../../services/firebase';

export const NotificationSettings = () => {
  const {
    isSupported,
    permission,
    isLoading,
    enableNotifications,
    disableNotifications,
    isEnabled
  } = usePushNotifications();

  const platformInfo = getPlatformInfo();
  
  // Debug logging
  console.log('NotificationSettings:', { isSupported, permission, platformInfo });

  // iOS Safari - needs to be added to Home Screen
  if (platformInfo.platform === 'ios_safari') {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Smartphone className="w-5 h-5" />
            Enable Push Notifications on iPhone/iPad
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-blue-700">
            To receive push notifications on iOS, add FendanaCor to your Home Screen:
          </p>
          <ol className="space-y-3">
            {platformInfo.instructions.map((instruction, idx) => (
              <li key={idx} className="flex items-start gap-3 text-sm text-blue-700">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center text-xs font-semibold">
                  {idx + 1}
                </span>
                <span className="pt-0.5">{instruction}</span>
              </li>
            ))}
          </ol>
          <div className="flex items-center gap-4 pt-2 border-t border-blue-200">
            <div className="flex items-center gap-2 text-blue-600">
              <Share className="w-5 h-5" />
              <span className="text-sm font-medium">Share</span>
            </div>
            <span className="text-blue-400">→</span>
            <div className="flex items-center gap-2 text-blue-600">
              <Plus className="w-5 h-5" />
              <span className="text-sm font-medium">Add to Home Screen</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Truly unsupported browser (not iOS Safari, not a supported browser)
  if (platformInfo.platform === 'unsupported') {
    return (
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <BellOff className="w-5 h-5" />
            Push Notifications Unavailable
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-amber-700">
            Your browser doesn&apos;t support push notifications. 
            Try using Chrome, Firefox, or Edge for the best experience.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-teal-600" />
          Push Notifications
          {isEnabled ? (
            <Badge variant="default" className="ml-2 bg-emerald-500">Enabled</Badge>
          ) : (
            <Badge variant="secondary" className="ml-2">Disabled</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Receive instant alerts for new referrals, updates, and appointments
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {permission === 'denied' ? (
          <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-rose-800">Notifications Blocked</p>
                <p className="text-sm text-rose-600 mt-1">
                  You have blocked notifications for this site. To enable them:
                </p>
                <ol className="text-sm text-rose-600 mt-2 list-decimal list-inside space-y-1">
                  <li>Click the lock/info icon in your browser&apos;s address bar</li>
                  <li>Find &quot;Notifications&quot; in the permissions</li>
                  <li>Change from &quot;Block&quot; to &quot;Allow&quot;</li>
                  <li>Refresh this page</li>
                </ol>
              </div>
            </div>
          </div>
        ) : isEnabled ? (
          <div className="space-y-4">
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="font-medium text-emerald-800">Notifications Active</p>
                  <p className="text-sm text-emerald-600">
                    You&apos;ll receive alerts for referral updates and appointments
                  </p>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={disableNotifications}
              className="w-full"
            >
              <BellOff className="w-4 h-4 mr-2" />
              Disable Notifications
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Enable push notifications to get instant alerts when:
            </p>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
                New referrals arrive (for consultants)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
                Referrals are accepted or need attention
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
                Appointments are scheduled
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
                Additional information is requested
              </li>
            </ul>
            <Button
              onClick={enableNotifications}
              disabled={isLoading}
              className="w-full bg-teal-600 hover:bg-teal-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enabling...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Enable Push Notifications
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
